import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { computed, nextTick, ref } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { useState } from '@/composable/useState';
import {
    EnumEventDateFilterType,
    EnumOddsCategoryType,
    EnumPresetFilterType,
    EnumTimeZoneType,
    useSimpleEventsQuery,
} from '@/core/oddsApi/oddsApiType';
import { SportType } from '@/interface/enum';

interface IUseEventsParam {
    sportType: MaybeRef<SportType>;
    eventIds?: MaybeRef<number[] | null>;
    leagueIds?: MaybeRef<number[] | null>;
    leagueNames?: MaybeRef<string[] | null>;
    isActive?: MaybeRef<boolean>;
}

export function useSimpleEvents({ sportType, eventIds, leagueIds, leagueNames, isActive = true }: IUseEventsParam) {
    const variables = computed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            filter: {
                presetFilter: EnumPresetFilterType.All,
                date: EnumEventDateFilterType.All,
            },
            oddsCategory: EnumOddsCategoryType.All,
            eventIds: get(eventIds),
            tournamentIds: get(leagueIds),
            tournamentNames: get(leagueNames),
            timeZone: EnumTimeZoneType.UTC__4,
        },
    }));

    const [isEnabled, setIsEnabled] = useState(true);
    const refetch = async () => {
        if (isEnabled.value) {
            setIsEnabled(false);
            nextTick(() => setIsEnabled(true));
        }
    };

    const simpleEvents = ref<number[]>([]);

    const { onResult, loaded, loading } = useSimpleEventsQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: get(isActive) && isEnabled.value,
        })),
    );

    onResult((result) => {
        simpleEvents.value = result?.map(x => x.id) ?? [];
    });

    return {
        loaded,
        loading,
        refetch,
        simpleEvents,
    };
}
